import type {
  IArticleStructuredData,
  IFAQStructuredData,
  IOrganizationStructuredData,
  ISoftwareAppStructuredData,
} from '@/interfaces';
import { BLOCKS } from '@contentful/rich-text-types';
import type { Block, Document, Inline, Text, TopLevelBlock } from '@contentful/rich-text-types';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

export type FAQItem = {
  question: string;
  answer: string;
};

export const ORGANIZATION_SCHEMA: IOrganizationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  url: 'https://www.parcelmonitor.com',
  sameAs: [
    'https://www.linkedin.com/company/parcelmonitor',
    'https://www.facebook.com/parcelmonitor',
    'https://www.instagram.com/parcelmonitortracking/',
    'https://x.com/ParcelMonitor',
    'https://apps.apple.com/us/app/parcel-monitor-track/id6738635254',
    'https://play.google.com/store/apps/details?id=com.parcelmonitor.Track',
  ],
  logo: `${publicRuntimeConfig.origin}/images/icons/schema_logo.png`,
  name: 'Parcel Monitor',
  description:
    'Parcel Monitor is the leading global platform for e-commerce logistics tracking, providing real-time updates for over 1,000 carriers worldwide.',
  email: 'tracking@parcelmonitor.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '138 Cecil Street',
    addressLocality: 'Singapore',
    addressCountry: 'SG',
    addressRegion: 'Singapore',
    postalCode: '069538',
  },
};

export const SOFTWARE_APP_SCHEMA: ISoftwareAppStructuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'Parcel Monitor - Track Packages',
      operatingSystem: 'iOS',
      applicationCategory: 'ShoppingApplication',
      installUrl: 'https://apps.apple.com/us/app/parcel-monitor-track/id6738635254',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        reviewCount: '4',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Parcel Monitor - Track Packages',
      operatingSystem: 'Android',
      applicationCategory: 'ShoppingApplication',
      installUrl: 'https://play.google.com/store/apps/details?id=com.parcelmonitor.Track',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.4',
        reviewCount: '113',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
  ],
};

const HEADING_TYPES: string[] = [
  BLOCKS.HEADING_1,
  BLOCKS.HEADING_2,
  BLOCKS.HEADING_3,
  BLOCKS.HEADING_4,
  BLOCKS.HEADING_5,
  BLOCKS.HEADING_6,
];

const normalizeText = (text: string): string => text.replace(/\s+/g, ' ').trim();

const groupBlocksByHeading = (blocks: TopLevelBlock[]): { heading: Block; body: TopLevelBlock[] }[] => {
  const groups: { heading: Block; body: TopLevelBlock[] }[] = [];

  for (const block of blocks) {
    if (HEADING_TYPES.includes(block.nodeType)) {
      groups.push({ heading: block, body: [] });
    } else {
      groups.at(-1)?.body.push(block);
    }
  }

  return groups;
};

export const extractTextFromNode = (node: Block | Inline | Text): string => {
  if (node.nodeType === 'text') {
    return node.value;
  }

  if ('content' in node && Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join('');
  }

  return '';
};

export const extractFaqItemsFromRichText = (faqContent: Document | undefined): FAQItem[] => {
  if (!faqContent?.content?.length) return [];

  return groupBlocksByHeading(faqContent.content)
    .map(({ heading, body }) => {
      const question = normalizeText(extractTextFromNode(heading));
      const answer = normalizeText(body.map(extractTextFromNode).join(' '));
      return question && answer ? { question, answer } : null;
    })
    .filter((item): item is FAQItem => item !== null);
};

/**
 * First paragraph of a rich-text document as plain text — used as the derived
 * excerpt on the blog listing and as the meta-description fallback of an article.
 */
export const extractExcerptFromRichText = (document: Document | undefined, maxLength = 200): string => {
  const firstParagraph = document?.content?.find((block) => block.nodeType === BLOCKS.PARAGRAPH);
  if (!firstParagraph) return '';

  const text = normalizeText(extractTextFromNode(firstParagraph));

  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
};

export const getArticleSchema = ({
  headline,
  datePublished,
  dateModified,
  image,
}: {
  headline: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
}): IArticleStructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  ...(datePublished && { datePublished }),
  ...(dateModified && { dateModified }),
  ...(image && { image }),
  author: [
    {
      '@type': 'Organization',
      name: 'Parcel Monitor',
      url: publicRuntimeConfig.origin,
    },
  ],
});

export const getFaqSchema = (faqItems: FAQItem[]): IFAQStructuredData | null => {
  if (faqItems.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };
};
